<template>
  <div class="h-listl">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-listl__content">
      <div class="h-listl__head">
        <div>
          <h1 class="h-listl__title">Customers</h1>
          <p class="h-listl__sub">{{ subtitle }}</p>
        </div>
      </div>

      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load customers"
        :message="error"
        @retry="load"
      />
      <HermesEmptyState
        v-else-if="!loading && !rows.length"
        variant="first-run"
        title="No customers yet"
        message="End users who purchase and manage subscriptions to your products appear here once they complete checkout."
      />
      <div v-else class="h-listl__card">
        <HermesTabulator
          :rows="rows"
          :columns="columns"
          :loading="loading"
          storage-key="admin-customers"
          :initial-sort="{ column: 'created', dir: 'desc' }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesCustomersList — Phase 5 lean customers (end-users) list island. Replaces
// the old 6-tab dashboard's `end_users` tab: a HermesTabulator over GET
// /admin/end-users.
export default {
  name: 'HermesCustomersList',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    vendorName: { type: String, default: '' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      rows: [],
      loading: true,
      error: null,
      crumbs: [{ label: 'Commerce' }, { label: 'Customers' }],
      columns: [
        { key: 'id', label: 'ID', width: 78, format: (v) => `<span style="font-family:var(--h-font-mono);color:var(--h-text-secondary)">#${v}</span>` },
        { key: 'email', label: 'Email' },
        { key: 'name', label: 'Name', format: (v) => v || '<span style="color:var(--h-text-tertiary)">—</span>' },
        { key: 'stripe_customer_id', label: 'Gateway customer', format: (v) => v ? `<span style="font-family:var(--h-font-mono);font-size:11.5px;color:var(--h-text-tertiary)">${v}</span>` : '<span style="color:var(--h-text-tertiary)">—</span>' },
        { key: 'last_login_at', label: 'Last login', width: 140, sorter: 'number', format: (v) => this.date(v) },
        { key: 'created', label: 'Created', width: 130, sorter: 'number', format: (v) => this.date(v) },
      ],
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const n = this.rows.length;
      return `${n} customer${n === 1 ? '' : 's'}`;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/end-users`);
        this.rows = data.items || [];
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load customers${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    date(v) {
      if (!v) return '<span style="color:var(--h-text-tertiary)">—</span>';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-listl { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-listl__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-listl__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-listl__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-listl__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-listl__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-listl__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-listl__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
</style>
